import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import api from '@/lib/api';
import { ArrowLeft, Plus, Trash2, Tag, Calendar as CalIcon, Users as UsersIcon, X, Megaphone, Sparkles } from 'lucide-react';
import { usePromo } from '@/lib/promoConfig';

// Mirrors the backend allowlists in `routes/admin.py:ALLOWED_SUB_PLANS` /
// `ALLOWED_EVENT_PLANS`. Keep in sync if either side changes.
const SUB_PLANS = [
  { id: 'verified_player_monthly', label: 'Verified Player · Monthly' },
  { id: 'verified_player_annual', label: 'Verified Player · Annual' },
  { id: 'instructor_plus_monthly', label: 'Instructor Advantage · Monthly' },
  { id: 'instructor_plus_annual', label: 'Instructor Advantage · Annual' },
  { id: 'instructor_unlimited_monthly', label: 'Instructor Unlimited · Monthly' },
  { id: 'instructor_unlimited_annual', label: 'Instructor Unlimited · Annual' },
];
const EVENT_PLANS = [
  { id: 'open_play', label: 'Open Play' },
  { id: 'verified_player', label: 'Verified Player' },
  { id: 'instructor_starter', label: 'Instructor Starter' },
  { id: 'instructor_plus', label: 'Instructor Advantage' },
  { id: 'instructor_unlimited', label: 'Instructor Unlimited' },
];

function emptyForm() {
  return {
    code: '',
    description: '',
    coupon_type: 'subscription',
    free_months: 3,
    free_event_posts: 6,
    applicable_plans: ['verified_player_monthly', 'verified_player_annual'],
    expires_at: '', // YYYY-MM-DD
  };
}

function fmtExpiry(iso) {
  if (!iso) return 'No expiry';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

export default function AdminCouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const { promo: livePromo, isPromoActive: liveActive, refresh: refreshPromo } = usePromo();
  const [promoteCode, setPromoteCode] = useState(null); // code of coupon being promoted; opens modal
  const [promoteForm, setPromoteForm] = useState({
    badge: 'Limited time',
    headline: '',
    body_suffix: '',
    ends_label: '',
  });
  const [promoting, setPromoting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/coupons');
      setCoupons(data.coupons || []);
    } catch {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePlan = (planId) => {
    setForm(f => ({
      ...f,
      applicable_plans: f.applicable_plans.includes(planId)
        ? f.applicable_plans.filter(p => p !== planId)
        : [...f.applicable_plans, planId],
    }));
  };

  const setType = (next) => {
    setForm(f => ({
      ...f,
      coupon_type: next,
      // Reset applicable_plans when the type changes since the allowlist differs.
      applicable_plans: next === 'subscription'
        ? ['verified_player_monthly', 'verified_player_annual']
        : ['instructor_starter', 'instructor_plus'],
    }));
  };

  const submitNew = async (e) => {
    e.preventDefault();
    const code = (form.code || '').trim().toUpperCase();
    if (!/^[A-Z0-9]{3,32}$/.test(code)) {
      toast.error('Code must be 3-32 characters, letters and numbers only.');
      return;
    }
    if (form.applicable_plans.length === 0) {
      toast.error('Pick at least one applicable plan.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code,
        description: form.description || '',
        coupon_type: form.coupon_type,
        applicable_plans: form.applicable_plans,
        expires_at: form.expires_at ? new Date(form.expires_at + 'T23:59:59Z').toISOString() : null,
      };
      if (form.coupon_type === 'subscription') {
        payload.free_months = Number(form.free_months);
      } else {
        payload.free_event_posts = Number(form.free_event_posts);
      }
      await api.post('/admin/coupons', payload);
      toast.success(`Coupon ${code} created!`);
      setCreating(false);
      setForm(emptyForm());
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create coupon');
    } finally {
      setSaving(false);
    }
  };

  const deleteCoupon = async (c) => {
    const force = c.usage_count > 0;
    const msg = force
      ? `Delete ${c.code}? It has ${c.usage_count} redemption(s) — historical records will be preserved but the code can no longer be used.`
      : `Delete ${c.code}? It has no redemptions and will be removed permanently.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/coupons/${c.code}${force ? '?force=true' : ''}`);
      toast.success(`Coupon ${c.code} deleted`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const extendExpiry = async (c) => {
    const input = window.prompt(
      `New expiry date for ${c.code} (YYYY-MM-DD). Leave blank to clear the expiry entirely:`,
      c.expires_at ? c.expires_at.slice(0, 10) : ''
    );
    if (input === null) return; // user hit cancel
    try {
      const payload = { expires_at: input.trim() ? new Date(input.trim() + 'T23:59:59Z').toISOString() : '' };
      await api.put(`/admin/coupons/${c.code}`, payload);
      toast.success(`Expiry updated for ${c.code}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  const openPromote = (c) => {
    setPromoteCode(c.code);
    setPromoteForm({
      badge: 'Limited time',
      headline: c.coupon_type === 'subscription'
        ? `${c.free_months || 0} months FREE`
        : `${c.free_event_posts || 0} free posts`,
      body_suffix: c.description || '',
      ends_label: c.expires_at
        ? `Ends ${new Date(c.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.`
        : '',
    });
  };

  const submitPromote = async (e) => {
    e.preventDefault();
    if (!promoteForm.headline.trim()) {
      toast.error('Headline is required.');
      return;
    }
    setPromoting(true);
    try {
      await api.post(`/admin/coupons/${promoteCode}/promote`, {
        badge: promoteForm.badge.trim(),
        headline: promoteForm.headline.trim(),
        body_suffix: promoteForm.body_suffix.trim(),
        ends_label: promoteForm.ends_label.trim(),
      });
      toast.success(`${promoteCode} is now live on the homepage!`);
      setPromoteCode(null);
      await refreshPromo();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not promote coupon');
    } finally {
      setPromoting(false);
    }
  };

  const stopPromotion = async () => {
    if (!window.confirm('Turn off the homepage promo? The marquee, homepage strip, and ribbons will all disappear immediately.')) return;
    try {
      await api.post('/admin/homepage-promo/disable');
      toast.success('Homepage promo turned off');
      await refreshPromo();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to disable promo');
    }
  };

  return (
    <div data-testid="admin-coupons-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#56dfb4]" data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4" /> Admin dashboard
          </Link>
          <h1 className="text-3xl font-bold mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Coupons</h1>
          <p className="text-sm text-slate-500 mt-1">
            Create, edit, and retire promotional codes without a code deploy.
          </p>
          {liveActive && (
            <div
              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#df5681]/10 border border-[#df5681]/30 text-xs flex-wrap"
              data-testid="live-promo-summary"
            >
              <Sparkles className="w-3.5 h-3.5 text-[#df5681]" />
              <span className="font-semibold text-[#df5681]">
                Currently live: <span className="font-mono">{livePromo.code}</span>
              </span>
              <span className="text-slate-600">— {livePromo.headline}</span>
              <a
                href="/?promo_preview=1"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1 inline-flex items-center gap-0.5 text-[#b456df] hover:text-[#9744bc] underline underline-offset-2"
                data-testid="preview-as-visitor-link"
              >
                Preview as visitor &rarr;
              </a>
            </div>
          )}
        </div>
        {!creating && (
          <Button
            onClick={() => setCreating(true)}
            className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] gap-1"
            data-testid="new-coupon-btn"
          >
            <Plus className="w-4 h-4" />
            New coupon
          </Button>
        )}
      </div>

      {creating && (
        <Card className="border-[#56dfb4]/30 bg-[#56dfb4]/5 mb-6" data-testid="new-coupon-form-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>New coupon</h2>
              <button
                onClick={() => { setCreating(false); setForm(emptyForm()); }}
                className="text-slate-400 hover:text-slate-600"
                data-testid="cancel-new-coupon-btn"
                aria-label="Cancel"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={submitNew} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs mb-1 block">Code</Label>
                  <Input
                    value={form.code}
                    onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 32) }))}
                    placeholder="SUMMER2026"
                    className="font-mono uppercase tracking-widest rounded-xl bg-white"
                    data-testid="new-coupon-code-input"
                    required
                  />
                  <p className="text-[11px] text-slate-500 mt-1">3-32 letters / numbers only.</p>
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Type</Label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setType('subscription')}
                      className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${form.coupon_type === 'subscription' ? 'bg-[#56dfb4] text-slate-900 border-[#56dfb4]' : 'border-slate-200 hover:bg-slate-50'}`}
                      data-testid="new-coupon-type-subscription"
                    >
                      Subscription (free months)
                    </button>
                    <button
                      type="button"
                      onClick={() => setType('event_posts')}
                      className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${form.coupon_type === 'event_posts' ? 'bg-[#56dfb4] text-slate-900 border-[#56dfb4]' : 'border-slate-200 hover:bg-slate-50'}`}
                      data-testid="new-coupon-type-event-posts"
                    >
                      Event posts
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs mb-1 block">Description (shown on emails / receipts)</Label>
                <Input
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value.slice(0, 300) }))}
                  placeholder="Summer 2026 — 2 free months for new Verified Player signups"
                  className="rounded-xl bg-white"
                  data-testid="new-coupon-description-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {form.coupon_type === 'subscription' ? (
                  <div>
                    <Label className="text-xs mb-1 block">Free months</Label>
                    <Input
                      type="number"
                      min="1"
                      max="24"
                      value={form.free_months}
                      onChange={e => setForm(f => ({ ...f, free_months: e.target.value }))}
                      className="rounded-xl bg-white"
                      data-testid="new-coupon-free-months-input"
                      required
                    />
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs mb-1 block">Free event posts</Label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={form.free_event_posts}
                      onChange={e => setForm(f => ({ ...f, free_event_posts: e.target.value }))}
                      className="rounded-xl bg-white"
                      data-testid="new-coupon-free-posts-input"
                      required
                    />
                  </div>
                )}
                <div>
                  <Label className="text-xs mb-1 block">Expires on (optional)</Label>
                  <Input
                    type="date"
                    value={form.expires_at}
                    onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
                    className="rounded-xl bg-white"
                    data-testid="new-coupon-expires-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs mb-2 block">Applicable plans</Label>
                <div className="flex flex-wrap gap-2">
                  {(form.coupon_type === 'subscription' ? SUB_PLANS : EVENT_PLANS).map(p => (
                    <label
                      key={p.id}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer text-xs ${form.applicable_plans.includes(p.id) ? 'bg-[#b456df]/10 border-[#b456df] text-[#b456df]' : 'border-slate-200 hover:bg-slate-50 text-slate-600'}`}
                      data-testid={`new-coupon-plan-${p.id}`}
                    >
                      <Checkbox
                        checked={form.applicable_plans.includes(p.id)}
                        onCheckedChange={() => togglePlan(p.id)}
                      />
                      {p.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  type="submit"
                  disabled={saving}
                  className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b]"
                  data-testid="save-new-coupon-btn"
                >
                  {saving ? 'Creating…' : 'Create coupon'}
                </Button>
                <Button
                  type="button"
                  onClick={() => { setCreating(false); setForm(emptyForm()); }}
                  variant="outline"
                  className="rounded-full"
                  disabled={saving}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin w-8 h-8 border-4 border-[#56dfb4] border-t-transparent rounded-full" />
        </div>
      ) : coupons.length === 0 ? (
        <Card className="border-slate-100">
          <CardContent className="p-10 text-center text-slate-500">
            <Tag className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p>No coupons yet. Click <span className="font-semibold">New coupon</span> to create the first one.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {coupons.map(c => (
            <Card key={c.code} className="border-slate-100" data-testid={`coupon-row-${c.code}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span
                        className="font-mono font-bold text-lg tracking-widest text-slate-800"
                        data-testid={`coupon-code-${c.code}`}
                      >
                        {c.code}
                      </span>
                      {liveActive && livePromo.code === c.code && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#df5681] text-white font-bold uppercase tracking-wider"
                          data-testid={`coupon-live-badge-${c.code}`}
                        >
                          <Sparkles className="w-3 h-3" /> Live on homepage
                        </span>
                      )}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.coupon_type === 'subscription' ? 'bg-[#b456df]/10 text-[#b456df]' : c.coupon_type === 'event_posts' ? 'bg-[#56c6df]/15 text-[#0f766e]' : 'bg-amber-50 text-amber-700'}`}>
                        {c.coupon_type || 'unknown'}
                      </span>
                      {c.coupon_type === 'subscription' && c.free_months ? (
                        <span className="text-xs text-slate-600">{c.free_months} months free</span>
                      ) : null}
                      {c.coupon_type === 'event_posts' && c.free_event_posts ? (
                        <span className="text-xs text-slate-600">{c.free_event_posts} free posts</span>
                      ) : null}
                    </div>
                    {c.description && <p className="text-sm text-slate-600 mb-2">{c.description}</p>}
                    <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <CalIcon className="w-3 h-3" />
                        Expires {fmtExpiry(c.expires_at)}
                      </span>
                      <span className="inline-flex items-center gap-1" data-testid={`coupon-usage-${c.code}`}>
                        <UsersIcon className="w-3 h-3" />
                        {c.usage_count} redemption{c.usage_count === 1 ? '' : 's'}
                      </span>
                      {Array.isArray(c.applicable_plans) && c.applicable_plans.length > 0 && (
                        <span className="text-[11px] text-slate-400">
                          Applies to: {c.applicable_plans.join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {liveActive && livePromo.code === c.code ? (
                      <Button
                        onClick={stopPromotion}
                        size="sm"
                        variant="outline"
                        className="rounded-full text-xs border-[#df5681] text-[#df5681] hover:bg-[#df5681]/10"
                        data-testid={`coupon-stop-promo-${c.code}`}
                      >
                        <Megaphone className="w-3 h-3 mr-1" /> Stop promotion
                      </Button>
                    ) : (
                      <Button
                        onClick={() => openPromote(c)}
                        size="sm"
                        variant="outline"
                        className="rounded-full text-xs"
                        data-testid={`coupon-promote-${c.code}`}
                        disabled={c.coupon_type !== 'subscription' && c.coupon_type !== 'event_posts'}
                        title={c.coupon_type !== 'subscription' && c.coupon_type !== 'event_posts'
                          ? 'Only subscription / event_posts coupons can be promoted on the homepage'
                          : 'Make this the active homepage promo'}
                      >
                        <Megaphone className="w-3 h-3 mr-1" /> Promote
                      </Button>
                    )}
                    <Button
                      onClick={() => extendExpiry(c)}
                      size="sm"
                      variant="outline"
                      className="rounded-full text-xs"
                      data-testid={`coupon-extend-${c.code}`}
                    >
                      Edit expiry
                    </Button>
                    <Button
                      onClick={() => deleteCoupon(c)}
                      size="sm"
                      variant="outline"
                      className="rounded-full text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                      data-testid={`coupon-delete-${c.code}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {promoteCode && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setPromoteCode(null); }}
          data-testid="promote-modal-backdrop"
        >
          <Card className="w-full max-w-lg border-[#df5681]/30" data-testid="promote-modal">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-[#df5681]" />
                  <h2 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Promote <span className="font-mono">{promoteCode}</span>
                  </h2>
                </div>
                <button
                  onClick={() => setPromoteCode(null)}
                  className="text-slate-400 hover:text-slate-600"
                  aria-label="Close"
                  data-testid="promote-modal-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-slate-500 mb-4">
                Going live will show this code in the homepage strip, the rotating marquee, and replace any other promoted coupon. Expiry is auto-pulled from the coupon itself.
              </p>
              <form onSubmit={submitPromote} className="space-y-3">
                <div>
                  <Label className="text-xs mb-1 block">Badge (small ribbon)</Label>
                  <Input
                    value={promoteForm.badge}
                    onChange={e => setPromoteForm(f => ({ ...f, badge: e.target.value.slice(0, 60) }))}
                    className="rounded-xl bg-white"
                    data-testid="promote-badge-input"
                  />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Headline <span className="text-red-500">*</span></Label>
                  <Input
                    value={promoteForm.headline}
                    onChange={e => setPromoteForm(f => ({ ...f, headline: e.target.value.slice(0, 120) }))}
                    placeholder="3 months FREE"
                    className="rounded-xl bg-white"
                    data-testid="promote-headline-input"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Body suffix</Label>
                  <Input
                    value={promoteForm.body_suffix}
                    onChange={e => setPromoteForm(f => ({ ...f, body_suffix: e.target.value.slice(0, 240) }))}
                    placeholder="Players get Verified Player, Instructors get Instructor Advantage."
                    className="rounded-xl bg-white"
                    data-testid="promote-body-input"
                  />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Ends label (small print)</Label>
                  <Input
                    value={promoteForm.ends_label}
                    onChange={e => setPromoteForm(f => ({ ...f, ends_label: e.target.value.slice(0, 60) }))}
                    placeholder="Ends Aug 31."
                    className="rounded-xl bg-white"
                    data-testid="promote-ends-input"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    type="submit"
                    disabled={promoting}
                    className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570]"
                    data-testid="promote-submit-btn"
                  >
                    {promoting ? 'Going live…' : 'Go live on homepage'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setPromoteCode(null)}
                    variant="outline"
                    className="rounded-full"
                    disabled={promoting}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
